import React, { useState, useEffect, useRef } from 'react';
import ModuleTopExtras from '../ModuleTopExtras';
import { useLanguage } from '../../i18n/LanguageContext';
import { API_ENDPOINTS, getApiUrl, apiRequest } from '../../config/api';
import { downloadText, htmlToPlainText } from '../../utils/download';
import { AdProcessingBanner } from '../ads/Ads';
import './Modules.css';
import 'katex/dist/katex.min.css';
import { InlineMath, BlockMath } from 'react-katex';

interface Toast {
  id: string;
  message: string;
  type: 'success' | 'error' | 'info';
}

interface Lecture {
  id: string;
  title: string;
  original_filename: string;
  file_size: number;
  total_pages: number;
  credit_cost: number;
  status: 'uploading' | 'processing' | 'completed' | 'failed';
  created_at: string;
  last_accessed: string | null;
  completed_at: string | null;
  error_message: string;
  pdf_file: string;
  processing_chunk_current?: number | null;
  processing_chunk_total?: number | null;
}

// Interface for lecture processing progress
interface LectureProgress {
  processingTime: number; // in seconds
  isProcessing: boolean;
}

interface LectureContent {
  id: string;
  content: string;
  created_at: string;
}

interface HtmlChunk {
  id: string;
  title: string;
  content: string;
}

const TrashIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14H5V6" />
    <line x1="10" y1="11" x2="10" y2="17" />
    <line x1="14" y1="11" x2="14" y2="17" />
    <polyline points="9 6 9 4 15 4 15 6" />
  </svg>
);

const CHUNK_MAX_CHARS = 4000;
// Keep in sync with the backend (MAX_UPLOAD_MB / LECTURE_VISION_MAX_PAGES).
const MAX_UPLOAD_MB = 25;
const MAX_LECTURE_PAGES = 80;

// Output language for the generated lecture. 'auto' detects it from the material;
// the other values force the language and override detection.
type LectureLanguage = 'auto' | 'english' | 'traditional_chinese' | 'simplified_chinese';

// KaTeX needs real characters — source HTML often carries escaped entities
// (&lt; &gt; &amp; ...) inside math spans, which KaTeX cannot parse.
// Decode &amp; last so double-escaped sequences (&amp;lt;) only unwrap once.
const decodeMathEntities = (tex: string): string =>
  tex
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#x27;|&#39;/g, "'")
    .replace(/&amp;/g, '&');

// Function to process HTML content and render math expressions
const processMathContent = (htmlContent: string): React.ReactNode => {
  // First, clean up any <br> tags that might have been inserted in math expressions
  let cleanedContent = htmlContent.replace(/(\$[^$]*?)<br[^>]*>([^$]*?\$)/g, '$1 $2');
  cleanedContent = cleanedContent.replace(/(\$\$[^$]*?)<br[^>]*>([^$]*?\$\$)/g, '$1 $2');
  
  // Handle HTML entities for <br> tags in math expressions
  cleanedContent = cleanedContent.replace(/&lt;br&gt;/g, '<br>');
  cleanedContent = cleanedContent.replace(/&lt;br\/&gt;/g, '<br>');
  
  // Handle matrix expressions with <br> tags - replace with \\ for proper LaTeX matrix formatting
  cleanedContent = cleanedContent.replace(/(\$\$[^$]*?)<br[^>]*>([^$]*?\$\$)/g, (match, before, after) => {
    // Check if this looks like a matrix (contains \begin{matrix} or \begin{pmatrix} etc.)
    if (before.includes('\\begin{') || after.includes('\\end{')) {
      return before + '\\\\' + after;
    }
    return before + ' ' + after;
  });
  
  // Handle matrix expressions with HTML entities
  cleanedContent = cleanedContent.replace(/(\$\$[^$]*?)&lt;br&gt;([^$]*?\$\$)/g, (match, before, after) => {
    // Check if this looks like a matrix (contains \begin{matrix} or \begin{pmatrix} etc.)
    if (before.includes('\\begin{') || after.includes('\\end{')) {
      return before + '\\\\' + after;
    }
    return before + ' ' + after;
  });
  
  // Handle math expressions wrapped in HTML spans like <span class="math">p_{B,B} = \frac{2}{3}</span>
  cleanedContent = cleanedContent.replace(/<span[^>]*class="[^"]*math[^"]*"[^>]*>((?:[^<]|<(?!\/span>))*?)<\/span>/g, (match, mathContent) => {
    // Check if the content looks like math (contains subscripts, superscripts, or LaTeX symbols)
    if (mathContent.includes('_') || mathContent.includes('^') || mathContent.includes('\\') || mathContent.includes('=') || mathContent.includes('<')) {
      return `$${mathContent}$`;
    }
    return match;
  });
  
  // Handle math expressions wrapped in HTML spans like <span class="key-term">P_{\delta_x}</span>
  cleanedContent = cleanedContent.replace(/<span[^>]*class="[^"]*key-term[^"]*"[^>]*>([^<]+)<\/span>/g, (match, mathContent) => {
    // Check if the content looks like math (contains subscripts, superscripts, or LaTeX symbols)
    if (mathContent.includes('_') || mathContent.includes('^') || mathContent.includes('\\')) {
      return `$${mathContent}$`;
    }
    return match;
  });
  
  // Handle ** markdown bold formatting
  cleanedContent = cleanedContent.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  
  // Handle KaTeX error content - clean up malformed math expressions and prepare for rendering
  cleanedContent = cleanedContent.replace(/<span class="katex-error"[^>]*>([^<]*)<\/span>/g, (match, errorContent) => {
    // Extract the actual math content from the error, removing HTML entities and malformed parts
    let cleanContent = errorContent
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&')
      .replace(/&quot;/g, '"')
      .replace(/&#x27;/g, "'")
      .replace(/<p><\/p>/g, '') // Remove empty paragraph tags
      .replace(/<p>/g, ' ') // Replace paragraph tags with spaces
      .replace(/<\/p>/g, ' ')
      .replace(/<[^>]*>/g, '') // Remove all HTML tags
      .replace(/\s+/g, ' ') // Normalize whitespace
      .trim();
    
    // Try to extract valid math expressions from the cleaned content
    const mathMatch = cleanContent.match(/\$[^$]*\$|\$\$[^$]*\$\$/);
    if (mathMatch) {
      // Return the math expression with proper delimiters for KaTeX rendering
      return mathMatch[0];
    }
    
    // If no valid math found, try to find LaTeX patterns and wrap them
    const latexMatch = cleanContent.match(/\\[a-zA-Z]+|_[a-zA-Z0-9]+|\^[a-zA-Z0-9]+|\\[a-zA-Z]+\{[^}]*\}|[a-zA-Z_]+_[a-zA-Z0-9]+|[a-zA-Z_]+\\[a-zA-Z]+/);
    if (latexMatch) {
      // Wrap potential LaTeX content in $ delimiters
      return `$${cleanContent}$`;
    }
    
    // If no math patterns found, return the cleaned content as plain text
    return cleanContent;
  });
  
  // Split content by math expressions - support $, $$, and LaTeX delimiters
  const parts = cleanedContent.split(/(\$\$[\s\S]*?\$\$|\$[^$]+\$|\\\[[\s\S]*?\\\]|\\\([\s\S]*?\\\))/);
  
  return parts.map((part, index) => {
    // Check if this part is a math expression
    if (part.startsWith('$$') && part.endsWith('$$')) {
      // Block math with $$ delimiters - remove the $$ delimiters
      const mathContent = decodeMathEntities(part.slice(2, -2).trim());
      return <BlockMath key={index} math={mathContent} renderError={(error) => (
        <span className="katex-error-fallback" style={{color: '#dc2626', fontFamily: 'monospace', fontSize: '0.9em'}}>
          Math Error: {mathContent}
        </span>
      )} />;
    } else if (part.startsWith('$') && part.endsWith('$') && part.length > 2) {
      // Inline math with single $ delimiters - remove the $ delimiters
      const mathContent = decodeMathEntities(part.slice(1, -1).trim());
      return <InlineMath key={index} math={mathContent} renderError={(error) => (
        <span className="katex-error-fallback" style={{color: '#dc2626', fontFamily: 'monospace', fontSize: '0.9em'}}>
          {mathContent}
        </span>
      )} />;
    } else if (part.startsWith('\\[') && part.endsWith('\\]')) {
      // Block math with LaTeX delimiters - remove the \[ and \] delimiters
      const mathContent = decodeMathEntities(part.slice(2, -2).trim());
      return <BlockMath key={index} math={mathContent} renderError={(error) => (
        <span className="katex-error-fallback" style={{color: '#dc2626', fontFamily: 'monospace', fontSize: '0.9em'}}>
          Math Error: {mathContent}
        </span>
      )} />;
    } else if (part.startsWith('\\(') && part.endsWith('\\)')) {
      // Inline math - remove the \( and \) delimiters
      const mathContent = decodeMathEntities(part.slice(2, -2).trim());
      return <InlineMath key={index} math={mathContent} renderError={(error) => (
        <span className="katex-error-fallback" style={{color: '#dc2626', fontFamily: 'monospace', fontSize: '0.9em'}}>
          {mathContent}
        </span>
      )} />;
    } else if (part.trim()) {
      // Regular HTML content
      return <span key={index} dangerouslySetInnerHTML={{ __html: part }} />;
    }
    return null;
  }).filter(Boolean);
};

// Scoped styles for the content layout (kept inside this component only)
const CONTENT_STYLES = `
  .lecture-content-layout{display:grid;grid-template-columns:260px 1fr;gap:24px;margin-top:16px}
  @media (max-width: 1024px){.lecture-content-layout{grid-template-columns:1fr}}
  .lecture-toc{position:sticky;top:84px;align-self:start;background:#ffffff;border:1px solid rgba(2,6,23,0.08);border-radius:12px;box-shadow:0 10px 30px -18px rgba(2,6,23,0.35);padding:14px}
  .lecture-toc h4{margin:4px 8px 8px 8px;color:#0f172a;font-size:14px;letter-spacing:0.02em}
  .lecture-toc .toc-list{list-style:none;margin:0;padding:0;max-height:60vh;overflow:auto}
  .lecture-toc .toc-item{display:flex;align-items:center;gap:8px;padding:8px 10px;border-radius:8px;cursor:pointer;color:#1f2937}
  .lecture-toc .toc-item:hover{background:rgba(37,99,235,0.08)}
  .lecture-toc .toc-item.active{background:rgba(37,99,235,0.14);color:#1d4ed8;font-weight:600}
  .lecture-toc .toc-index{background:#1d4ed8;color:#fff;border-radius:6px;font-size:12px;padding:2px 6px}

  .lecture-main-pane{min-width:0}
  .lecture-note{background:linear-gradient(135deg,rgba(16,185,129,0.12),rgba(59,130,246,0.10));border:1px solid rgba(16,185,129,0.28);color:#065f46;border-radius:12px;padding:12px 14px;margin-bottom:16px;box-shadow:0 18px 36px -24px rgba(16,185,129,0.45)}
  .lecture-note strong{color:#047857}

  .lecture-section-card{background:#ffffff;border:1px solid rgba(2,6,23,0.08);border-radius:14px;overflow:hidden;box-shadow:0 24px 36px -28px rgba(2,6,23,0.5)}
  .lecture-section-card .section-card-header{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:12px 16px;background:linear-gradient(180deg,rgba(37,99,235,0.08),rgba(37,99,235,0.02));border-bottom:1px solid rgba(2,6,23,0.06)}
  .lecture-section-card .section-card-header h3{margin:0;color:#1d4ed8}
  .lecture-section-card .section-badges{display:flex;flex-wrap:wrap;gap:8px}
  .lecture-section-card .badge{display:inline-flex;align-items:center;gap:6px;border-radius:999px;padding:6px 10px;font-size:12px;border:1px solid rgba(2,6,23,0.12);background:#fff;color:#0f172a}
  .lecture-section-card .badge .dot{width:8px;height:8px;border-radius:999px}
  .badge-blue .dot{background:#2563eb}
  .badge-amber .dot{background:#f59e0b}
  .badge-emerald .dot{background:#10b981}
  .badge-fuchsia .dot{background:#d946ef}
  .badge-slate .dot{background:#475569}
  .lecture-section-card .section-card-body{padding:18px}

  /* Ensure generated content remains readable with proper spacing */
  .lecture-content-body h1,.lecture-content-body h2,.lecture-content-body h3,.lecture-content-body h4{margin:1.5rem 0 0.75rem;color:#1d4ed8;font-weight:600}
  .lecture-content-body p{line-height:1.8;margin:1rem 0;text-align:justify}
  .lecture-content-body ul,.lecture-content-body ol{margin:1rem 0 1rem 1.5rem;line-height:1.7}
  .lecture-content-body li{margin:0.5rem 0}
  .lecture-content-body code{background:rgba(2,6,23,0.06);padding:0.1rem 0.3rem;border-radius:4px;font-family:monospace}
  .lecture-content-body .lecture-heading-wrapper{margin:2rem 0 1rem 0}
  .lecture-content-body .lecture-main-title{color:#1e40af;font-size:1.5rem;font-weight:700;margin:2rem 0 1rem 0;border-bottom:2px solid #e5e7eb;padding-bottom:0.5rem}
  .lecture-content-body .lecture-section{color:#2563eb;font-size:1.25rem;font-weight:600;margin:1.75rem 0 0.75rem 0;border-left:4px solid #3b82f6;padding-left:1rem}
  .lecture-content-body .lecture-subsection{color:#1d4ed8;font-size:1.1rem;font-weight:600;margin:1.5rem 0 0.5rem 0}
  .lecture-content-body .lecture-subheading{color:#2563eb;font-weight:600;margin:1.25rem 0 0.5rem 0;font-size:1rem}
  
  /* Math expression styling */
  .lecture-content-body .katex-display{margin:1.5rem 0;text-align:center;overflow-x:auto}
  .lecture-content-body .katex{font-size:1.1em}
  .lecture-content-body .katex-display .katex{font-size:1.2em}
  .lecture-content-body .katex-error{color:#dc2626;background:#fef2f2;padding:0.5rem;border-radius:4px;border:1px solid #fecaca}
  .lecture-content-body .katex-error-fallback{color:#dc2626;background:#fef2f2;padding:0.25rem 0.5rem;border-radius:4px;border:1px solid #fecaca;display:inline-block;margin:0.25rem 0}
`;

// Inject minimal styles for readability (tables, highlights)
const ensureBaseStyles = (html: string): string => {
  const styleTag = `<style>
    table{border-collapse:collapse;width:100%;}table,th,td{border:1px solid #ccc;}th,td{padding:6px;}
    .lecture-highlight{color:#d14;font-weight:bold;background:rgba(209,20,20,0.08);padding:0 2px;border-radius:2px}
    mark{background:#fff59d;padding:0 2px;border-radius:2px}
    .lecture-content-body .key-term{color:#2563eb;font-weight:700;background:rgba(37,99,235,0.1);padding:0 0.15rem;border-radius:4px}
    .lecture-content-body mark{background:rgba(16,185,129,0.2);color:#047857;padding:0.1rem 0.3rem;border-radius:4px;font-weight:600}
    .lecture-content-body .concept-box{background:linear-gradient(135deg,rgba(96,165,250,0.18),rgba(167,243,208,0.18));border:1px solid rgba(37,99,235,0.25);padding:1rem;margin:1.5rem 0;border-radius:12px;box-shadow:0 12px 24px -16px rgba(37,99,235,0.45)}
    .lecture-content-body .concept-box h3,.lecture-content-body .concept-box h4{color:#1d4ed8;margin-top:0}
    .lecture-content-body table{width:100%;border-collapse:collapse;margin:1rem 0;background:rgba(30,64,175,0.05);border-radius:8px;overflow:hidden}
    .lecture-content-body table th,.lecture-content-body table td{border:1px solid rgba(30,64,175,0.2);padding:0.75rem 1rem;text-align:left}
    .lecture-content-body table th{background:rgba(30,64,175,0.18);color:#1d4ed8;font-weight:700}
    .lecture-content-body .lecture-table{width:100%;border-collapse:collapse;margin:1rem 0;background:rgba(30,64,175,0.05);border-radius:8px;overflow:hidden}
    .lecture-content-body .lecture-table th,.lecture-content-body .lecture-table td{border:1px solid rgba(30,64,175,0.2);padding:0.75rem 1rem;text-align:left}
  .lecture-content-body .lecture-table th{background:rgba(30,64,175,0.18);color:#1d4ed8;font-weight:700}
  .lecture-content-body blockquote{border-left:4px solid #10b981;background:rgba(16,185,129,0.1);color:#047857;padding:0.75rem 1rem;margin:1.5rem 0;border-radius:8px}
  
  /* Processing Timer Styles */
  .processing-status{margin:16px 0;text-align:center}
  .processing-timer{display:flex;align-items:center;justify-content:center;gap:8px;padding:12px 16px;background:rgba(37,99,235,0.1);border-radius:8px;border:1px solid rgba(37,99,235,0.2)}
  .timer-icon{font-size:18px}
  .timer-text{font-size:16px;font-weight:600;color:#1d4ed8}
  </style>`;
  if (!html) return styleTag;
  return /^<style>[\s\S]*?<\/style>/.test(html) ? html : styleTag + html;
};

// Clean model output, remove fences/intro, keep only displayable HTML
const cleanGeneratedHtml = (raw: string): string => {
  if (!raw) return '';
  let cleaned = raw.trim();
  // Remove markdown fences
  cleaned = cleaned.replace(/^```\s*html\s*/i, '').replace(/^```\s*/i, '').replace(/```\s*$/i, '');
  // Remove common intro text the model may add
  cleaned = cleaned
    .replace(/^(?:Here'?s\s+an?\s+overview[^<]*?<br?>?)/i, '')
    .replace(/^(?:Below\s+is[^<]*?<br?>?)/i, '')
    .replace(/^(?:The\s+user\s+asked:[\s\S]*?)(<br\/?\s*>)+/i, '')
    .replace(/^(?:User\s+Query:?[\s\S]*?)(<br\/?\s*>)+/i, '')
    .replace(/^(?:Prompt:?[\s\S]*?)(<br\/?\s*>)+/i, '');
  // Strip leftover triple backticks inside
  cleaned = cleaned.replace(/```+/g, '');
  // Normalize whitespace
  cleaned = cleaned.replace(/\n{2,}/g, '\n\n');
  return cleaned || '';
};

const isLikelyHtml = (text: string): boolean => /<\/?[a-z][\s\S]*>/i.test(text);

const toDisplayHtml = (raw: string): string => {
  const cleaned = cleanGeneratedHtml(raw);
  const html = isLikelyHtml(cleaned) ? cleaned : convertMarkdownToHtml(cleaned);
  const normalized = normalizeHtmlForDisplay(html);
  return ensureBaseStyles(normalized);
};

const stripHtml = (value: string): string => value.replace(/<[^>]+>/g, '').trim();

const formatInline = (text: string): string => {
  let formatted = text;
  formatted = formatted.replace(/\*\*(.+?)\*\*/g, '<span class="lecture-highlight">$1</span>');
  formatted = formatted.replace(/\*(.+?)\*/g, '<em>$1</em>');
  formatted = formatted.replace(/`(.+?)`/g, '<code>$1</code>');
  return formatted;
};

// Generate meaningful title from content
const generateContentTitle = (content: string): string => {
  if (!content) return 'Content';

  // Strip HTML and get clean text
  const cleanText = content.replace(/<[^>]*>/g, '').trim();
  if (!cleanText) return 'Content';
  
  // Split into sentences and take the first meaningful sentence
  const sentences = cleanText.split(/[.!?。！？]/).filter(s => s.trim().length > 10);
  
  if (sentences.length === 0) {
    // If no sentences, take first 50 characters
    return cleanText.length > 50 ? cleanText.substring(0, 47) + '...' : cleanText;
  }
  
  // Take the first sentence, limit to 60 characters
  const firstSentence = sentences[0].trim();
  if (firstSentence.length <= 60) {
    return firstSentence;
  }
  
  // If too long, truncate at word boundary
  const truncated = firstSentence.substring(0, 57);
  const lastSpace = truncated.lastIndexOf(' ');
  return lastSpace > 30 ? truncated.substring(0, lastSpace) + '...' : truncated + '...';
};

// Extract key concepts from content for better titles
const extractKeyConcepts = (content: string): string[] => {
  const cleanText = content.replace(/<[^>]*>/g, '').toLowerCase();
  
  // Look for common academic patterns
  const patterns = [
    /(?:introduction|overview|summary|conclusion)/gi,
    /(?:definition|definitions?|define)/gi,
    /(?:example|examples?|for instance)/gi,
    /(?:analysis|analyze|analysis of)/gi,
    /(?:theory|theories|theoretical)/gi,
    /(?:method|methods|methodology)/gi,
    /(?:application|applications?|apply)/gi,
    /(?:principle|principles?|fundamental)/gi,
    /(?:concept|concepts?|idea|ideas)/gi,
    /(?:process|processes?|procedure)/gi,
    /(?:characteristic|characteristics?|feature|features)/gi,
    /(?:advantage|advantages?|benefit|benefits)/gi,
    /(?:disadvantage|disadvantages?|limitation|limitations)/gi,
    /(?:comparison|compare|contrast)/gi,
    /(?:relationship|relationships?|connection|connections)/gi
  ];
  
  const concepts: string[] = [];
  patterns.forEach(pattern => {
    const matches = cleanText.match(pattern);
    if (matches) {
      concepts.push(...matches.map(m => m.toLowerCase()));
    }
  });
  
  return [...new Set(concepts)]; // Remove duplicates
};

const normalizeHtmlForDisplay = (html: string): string => {
  // First, protect math expressions from being modified - handle multiline math expressions
  const mathPlaceholder = '___MATH_PLACEHOLDER___';
  const mathExpressions: string[] = [];
  let processed = html.replace(/(\$\$[\s\S]*?\$\$|\$[^$\n]+(?:\n[^$\n]+)*\$|\\\[[\s\S]*?\\\]|\\\([\s\S]*?\\\))/g, (match) => {
    mathExpressions.push(match);
    return mathPlaceholder;
  });

  // Then, protect tables from being wrapped in paragraphs
  const tablePlaceholder = '___TABLE_PLACEHOLDER___';
  const tables: string[] = [];
  processed = processed.replace(/<table[\s\S]*?<\/table>/gi, (match) => {
    tables.push(match);
    return tablePlaceholder;
  });

  // Now handle paragraph spacing for non-table content
  processed = processed
    .replace(/\n{3,}/g, '\n\n') // Normalize multiple line breaks
    .replace(/\n\n/g, '</p><p>') // Convert double line breaks to paragraph breaks
    .replace(/^/, '<p>') // Add opening paragraph tag
    .replace(/$/, '</p>'); // Add closing paragraph tag

  // Handle headings properly with better subsection support
  processed = processed
    .replace(/(<br\/>)?####\s+([^<\n]+)/g, '<h4 class="lecture-subheading">$2</h4>')
    .replace(/(<br\/>)?###\s+([^<\n]+)/g, '<h3 class="lecture-subsection">$2</h3>')
    .replace(/(<br\/>)?##\s+([^<\n]+)/g, '<h2 class="lecture-section">$2</h2>')
    .replace(/(<br\/>)?#\s+([^<\n]+)/g, '<h1 class="lecture-main-title">$2</h1>');

  // Clean up any remaining line breaks and ensure proper spacing
  processed = processed
    .replace(/<br\/>\s*(<h[1-4])/g, '$1') // Remove line breaks before headings
    .replace(/(<h[1-4][^>]*>.*?<\/h[1-4]>)<p>/g, '$1<p>') // Ensure headings aren't wrapped in paragraphs
    .replace(/<\/p><p><\/p>/g, '</p><p>') // Remove empty paragraphs
    .replace(/<p><\/p>/g, '') // Remove empty paragraphs
    .replace(/<p>\s*<\/p>/g, '') // Remove whitespace-only paragraphs
    .replace(/(<h[1-4][^>]*>.*?<\/h[1-4]>)<p>/g, '$1<p>'); // Ensure proper spacing after headings

  // Wrap headings in proper containers with better subsection handling
  processed = processed.replace(/(<h[1-4][^>]*>.*?<\/h[1-4]>)/g, '<div class="lecture-heading-wrapper">$1</div>');

  // Restore math expressions first
  let mathIndex = 0;
  processed = processed.replace(new RegExp(mathPlaceholder, 'g'), () => {
    const math = mathExpressions[mathIndex++];
    return math;
  });

  // Restore tables and clean up spacing around them
  let tableIndex = 0;
  processed = processed.replace(new RegExp(tablePlaceholder, 'g'), () => {
    const table = tables[tableIndex++];
    return table;
  });

  // Clean up excessive spacing around tables
  processed = processed
    .replace(/(<\/p>)\s*(<table)/gi, '$1\n$2') // Ensure single line break before tables
    .replace(/(<\/table>)\s*(<p>)/gi, '$1\n$2') // Ensure single line break after tables
    .replace(/(<\/p>)\s*(\n\s*){2,}(<table)/gi, '$1\n$3') // Remove multiple line breaks before tables
    .replace(/(<\/table>)\s*(\n\s*){2,}(<p>)/gi, '$1\n$3') // Remove multiple line breaks after tables
    .replace(/\n\s*\n\s*\n/g, '\n\n'); // Normalize multiple line breaks to double

  return `<div class="lecture-content-body">${processed}</div>`;
};

const getContentSignals = (html: string) => {
  const lower = html.toLowerCase();
  return {
    hasTable: /<table[\s\S]*?>/i.test(lower),
    hasList: /<(ul|ol)[\s\S]*?>/i.test(lower),
    hasCode: /<(pre|code)[\s\s]*?>/i.test(lower),
    hasQuote: /<blockquote[\s\S]*?>/i.test(lower),
  };
};

const convertMarkdownToHtml = (markdown: string): string => {
  const lines = markdown.replace(/\r\n/g, '\n').split('\n');
  let html = '';
  let listType: 'ul' | 'ol' | null = null;
  let inParagraph = false;

  const closeList = () => {
    if (listType) {
      html += listType === 'ul' ? '</ul>' : '</ol>';
      listType = null;
    }
  };

  const closeParagraph = () => {
    if (inParagraph) {
      html += '</p>';
      inParagraph = false;
    }
  };

  lines.forEach((line, index) => {
    const trimmed = line.trim();
    
    // Handle empty lines - close current paragraph
    if (!trimmed) {
      closeList();
      closeParagraph();
      return;
    }

    const headingMatch = trimmed.match(/^#{1,6}\s+/);
    if (headingMatch) {
      closeList();
      closeParagraph();
      const level = Math.min(headingMatch[0].length, 4);
      const headingTag = `h${level + 1}`;
      const text = formatInline(trimmed.replace(/^#{1,6}\s*/, ''));
      
      // Add appropriate CSS classes for better subsection styling
      let cssClass = 'lecture-heading';
      if (level === 0) cssClass = 'lecture-main-title';
      else if (level === 1) cssClass = 'lecture-section';
      else if (level === 2) cssClass = 'lecture-subsection';
      else if (level === 3) cssClass = 'lecture-subheading';
      
      html += `<${headingTag} class="${cssClass}">${text}</${headingTag}>`;
      return;
    }

    if (/^>\s+/.test(trimmed)) {
      closeList();
      closeParagraph();
      html += `<blockquote>${formatInline(trimmed.replace(/^>\s+/, ''))}</blockquote>`;
      return;
    }

    if (/^\d+\.\s+/.test(trimmed)) {
      closeParagraph();
      if (listType !== 'ol') {
        closeList();
        html += '<ol>';
        listType = 'ol';
      }
      html += `<li>${formatInline(trimmed.replace(/^\d+\.\s+/, ''))}</li>`;
      return;
    }

    if (/^[-*]\s+/.test(trimmed)) {
      closeParagraph();
      if (listType !== 'ul') {
        closeList();
        html += '<ul>';
        listType = 'ul';
      }
      html += `<li>${formatInline(trimmed.replace(/^[-*]\s+/, ''))}</li>`;
      return;
    }

    if (/^---+$/.test(trimmed)) {
      closeList();
      closeParagraph();
      html += '<hr />';
      return;
    }

    // Regular text - start or continue paragraph
    if (!inParagraph) {
      html += '<p>';
      inParagraph = true;
    } else {
      html += ' '; // Add space between lines in same paragraph
    }
    html += formatInline(trimmed);
  });

  closeList();
  closeParagraph();
  return html;
};

const splitHtmlIntoChunks = (html: string): HtmlChunk[] => {
  if (!html) return [];

  // Remove any <style> blocks injected by ensureBaseStyles
  const withoutStyle = html.replace(/<style[\s\S]*?<\/style>/gi, '');
  
  // Strip the outer wrapper if present
  const inner = withoutStyle
    .replace(/^<div class="lecture-content-body">/i, '')
    .replace(/<\/div>\s*$/i, '');

  // Protect math blocks from newline-to-<br> conversion to avoid injecting <br> into LaTeX
  const MATH_PLACEHOLDER = '___MATH_PLACEHOLDER___';
  const mathBlocks: string[] = [];
  const protectedInner = inner.replace(/(\$\$[\s\S]*?\$\$|\$[^$]+(?:\n[^$]+)*\$|\\\[[\s\S]*?\\\]|\\\([\s\S]*?\\\))/g, (match) => {
    mathBlocks.push(match);
    return MATH_PLACEHOLDER;
  });
  const withBr = protectedInner.replace(/\n/g, '<br>');
  let mathIndex = 0;
  const normalized = withBr.replace(new RegExp(MATH_PLACEHOLDER, 'g'), () => mathBlocks[mathIndex++]);
  const sections: HtmlChunk[] = [];

  // Split on headings (h1-h5) while keeping the heading with each segment
  const segments = normalized.split(/(?=<h[1-5][^>]*>)/gi).filter(Boolean);

  if (segments.length === 0) {
    // No headings found, try to create meaningful chunks with good titles
    return chunkByLength(normalized);
  }

  segments.forEach((segment, idx) => {
    if (!segment.trim()) return;

    const titleMatch = segment.match(/<h[1-5][^>]*>(.*?)<\/h[1-5]>/i);
    let titleText = titleMatch ? stripHtml(titleMatch[1]) : '';

    // Remove the heading from the content to get just the body
    const bodyHtml = segment.replace(/<h[1-5][^>]*>.*?<\/h[1-5]>/i, '').trim();
    
    // Check for visible text content (strip all HTML tags and check)
    const visibleText = bodyHtml.replace(/<[^>]*>/g, '').trim();
    
    // Skip empty sections or sections with only whitespace
    if (!visibleText) {
      return;
    }

    // If no title from heading, generate one from content
    if (!titleText) {
      titleText = generateContentTitle(bodyHtml);
    }

    if (bodyHtml.length <= CHUNK_MAX_CHARS) {
      sections.push({
        id: `chunk-${sections.length + 1}`,
        title: titleText,
        content: bodyHtml,
      });
    } else {
      const subchunks = chunkByLength(bodyHtml, titleText);
      sections.push(...subchunks);
    }
  });

  // If we have sections with content, return them
  if (sections.length > 0) {
    return sections;
  }
  
  // Fallback: if no sections with content, try chunkByLength
  const fallbackChunks = chunkByLength(normalized);
  
  // If fallback also has no content, return empty array
  if (fallbackChunks.length === 0) {
    return [];
  }
  
  // If we have content but no clear sections, make the first chunk more descriptive
  if (fallbackChunks.length > 0 && fallbackChunks[0].title.startsWith('Section')) {
    const firstChunk = fallbackChunks[0];
    const concepts = extractKeyConcepts(firstChunk.content);
    
    if (concepts.length > 0) {
      // Use the first concept as a better title
      const concept = concepts[0].charAt(0).toUpperCase() + concepts[0].slice(1);
      firstChunk.title = concept;
    } else {
      // Generate a title from the content
      firstChunk.title = generateContentTitle(firstChunk.content);
    }
  }
  
  return fallbackChunks;
};

const chunkByLength = (html: string, heading?: string): HtmlChunk[] => {
  // Remove any <style> blocks first
  const cleaned = html.replace(/<style[\s\S]*?<\/style>/gi, '');
  const parts = cleaned.split(/<\s*br\s*\/?>|<\/p>/i).filter(part => part.trim() !== '');
  const chunks: HtmlChunk[] = [];
  let buffer = '';

  parts.forEach((part) => {
    const trimmed = part.trim();
    const candidate = buffer ? `${buffer}<br>${trimmed}` : trimmed;

    if (candidate.length > CHUNK_MAX_CHARS && buffer) {
      // Only add chunk if it has meaningful content
      const cleanBuffer = buffer.replace(/<[^>]*>/g, '').trim();
      if (cleanBuffer) {
        // Generate meaningful title
        let title = heading;
        if (!title) {
          title = generateContentTitle(buffer);
        } else if (chunks.length > 0) {
          title = `${heading} (continued)`;
        }
        
        chunks.push({
          id: `chunk-${chunks.length + 1}`,
          title: title,
          content: buffer,
        });
      }
      buffer = trimmed;
    } else {
      buffer = candidate;
    }
  });

  if (buffer) {
    // Only add final chunk if it has meaningful content
    const cleanBuffer = buffer.replace(/<[^>]*>/g, '').trim();
    if (cleanBuffer) {
      // Generate meaningful title
      let title = heading;
      if (!title) {
        title = generateContentTitle(buffer);
      } else if (chunks.length > 0) {
        title = `${heading} (continued)`;
      }
      
      chunks.push({
        id: `chunk-${chunks.length + 1}`,
        title: title,
        content: buffer,
      });
    }
  }

  return chunks;
};

// (keep single definition above)


const LectureModule: React.FC = () => {
  const { t } = useLanguage();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const txtImportRef = useRef<HTMLInputElement>(null);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [currentView, setCurrentView] = useState<'main' | 'upload' | 'content'>('main');
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  // Default follows the material; pick 繁中/简中/English to force it if auto misfires.
  const [outputLanguage, setOutputLanguage] = useState<LectureLanguage>('auto');
  const [lectures, setLectures] = useState<Lecture[]>([]);
  const [selectedLecture, setSelectedLecture] = useState<Lecture | null>(null);
  const [lectureContent, setLectureContent] = useState<LectureContent | null>(null);
  const [contentChunks, setContentChunks] = useState<HtmlChunk[]>([]);
  const [activeChunkIndex, setActiveChunkIndex] = useState(0);
  const [defaultHtmlContent, setDefaultHtmlContent] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [lectureToDelete, setLectureToDelete] = useState<Lecture | null>(null);
  const [editingTitle, setEditingTitle] = useState<string | null>(null);
  const [editTitleValue, setEditTitleValue] = useState('');
  const [processingLecture, setProcessingLecture] = useState<Lecture | null>(null);
  const [lectureProgress, setLectureProgress] = useState<LectureProgress>({
    processingTime: 0,
    isProcessing: false
  });

  const openPdfInNewTab = async (lectureId: string, filename?: string | null) => {
    const pdfWindow = window.open('', '_blank', 'noopener,noreferrer');
    if (pdfWindow) {
      pdfWindow.document.write('<p style="font-family: sans-serif; padding: 16px;">Loading PDF…</p>');
    }

    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.LECTURE_PDF(lectureId)), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
        credentials: 'include',
      });
      if (!response.ok) {
        showToast('Failed to load PDF', 'error');
        return;
      }

      const pdfBlob = await response.blob();
      const url = URL.createObjectURL(pdfBlob);

      if (pdfWindow) {
        pdfWindow.location.href = url;
      } else {
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        if (filename) {
          link.download = filename;
        }
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (error) {
      console.error('Error opening PDF', error);
      if (pdfWindow) {
        pdfWindow.document.body.innerHTML = '<p style="font-family: sans-serif; padding: 16px; color: #d9534f;">Failed to load PDF. Please close this tab.</p>';
      }
      showToast('Unable to open PDF', 'error');
    }
  };

  // Toast functionality
  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    const id = Date.now().toString();
    const newToast: Toast = { id, message, type };
    setToasts(prev => [...prev, newToast]);

    setTimeout(() => {
      setToasts(prev => prev.filter(toast => toast.id !== id));
    }, 5000);
  };


  // Real-time timer for processing
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    
    if (lectureProgress.isProcessing) {
      interval = setInterval(() => {
        setLectureProgress(prev => ({
          ...prev,
          processingTime: prev.processingTime + 1
        }));
      }, 1000);
    }
    
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [lectureProgress.isProcessing]);

  const loadLectures = async (page = 1, search = '') => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        ...(search && { search }),
      });
      
      const url = getApiUrl(API_ENDPOINTS.LECTURE_LIST) + `?${params}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        // Handle both paginated and non-paginated responses
        const lecturesData = data.results || data;
        const normalizedLectures = Array.isArray(lecturesData) ? lecturesData : [];
        setLectures(normalizedLectures);
        setTotalPages(data.total_pages || 1);
        setCurrentPage(page);

        const activeProcessing = normalizedLectures.find((lecture: Lecture) => lecture.status !== 'completed');
        setProcessingLecture(activeProcessing || null);
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Lecture list error response:', response.status, errorData);
        showToast('Failed to load lectures', 'error');
      }
    } catch (error) {
      console.error('Error loading lectures:', error);
      showToast('Error loading lectures', 'error');
    }
  };


  // REVAMP: the stateless endpoint generates and RETURNS the lecture in one
  // response (nothing is stored). Consume it directly — no polling/history.
  const handleFileUpload = async (file: File) => {
    if (!file) return;

    if (file.size > MAX_UPLOAD_MB * 1024 * 1024) {
      showToast(`File size must be under ${MAX_UPLOAD_MB}MB`, 'error');
      return;
    }

    if (!file.name.toLowerCase().endsWith('.pdf')) {
      showToast('Only PDF files are allowed', 'error');
      return;
    }

    setUploading(true);
    setLectureProgress({ processingTime: 0, isProcessing: true });

    // Vision is now the only reading path — every page is read by the
    // DeepSeek vision model, so there is no text/visual mode to send.
    const form = new FormData();
    form.append('pdf_file', file);
    form.append('title', file.name.replace(/\.pdf$/i, ''));
    form.append('language', outputLanguage);

    try {
      const res = await apiRequest<{
        title: string;
        lecture: string;
        total_pages: number;
        language?: string;
        error?: string;
        vision?: { pages_read?: number; calls?: number; truncated?: boolean; max_pages?: number };
      }>(
        API_ENDPOINTS.LECTURE_UPLOAD,
        { method: 'POST', body: form, timeout: 15 * 60 * 1000 }
      );

      if (!res.data?.lecture) {
        showToast(res.data?.error || 'No lecture content was generated.', 'error');
        return;
      }

      const visionMeta = res.data.vision;
      if (visionMeta?.truncated) {
        const cap = visionMeta.max_pages || MAX_LECTURE_PAGES;
        showToast(`Only the first ${visionMeta.pages_read || cap} of ${res.data.total_pages} pages were read (limit ${cap}).`, 'info');
      }

      const title = res.data.title || file.name.replace(/\.pdf$/i, '');
      const sessionLecture: Lecture = {
        id: 'session',
        title,
        original_filename: file.name,
        file_size: file.size,
        total_pages: res.data.total_pages || 0,
        credit_cost: 0,
        status: 'completed',
        created_at: new Date().toISOString(),
        last_accessed: null,
        completed_at: new Date().toISOString(),
        error_message: '',
        pdf_file: '',
      };
      setLectureContent({ id: 'session', content: res.data.lecture, created_at: sessionLecture.created_at });
      setSelectedLecture(sessionLecture);
      const htmlContent = toDisplayHtml(res.data.lecture);
      setDefaultHtmlContent(htmlContent);
      setContentChunks(splitHtmlIntoChunks(htmlContent));
      setActiveChunkIndex(0);
      setCurrentView('content');
      showToast('Lecture generated.', 'success');
    } catch (error) {
      console.error('Upload error:', error);
      const msg = error instanceof Error ? error.message : 'Upload failed';
      showToast(msg.includes('No readable content') ? "No readable content was found in that PDF — please check the file and try again." : msg, 'error');
    } finally {
      setUploading(false);
      setLectureProgress({ processingTime: 0, isProcessing: false });
    }
  };

  const fetchLatestLecture = async (lectureId: string): Promise<Lecture | null> => {
    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.LECTURE_DETAIL(lectureId)), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
      });

      if (!response.ok) {
        showToast('Unable to refresh lecture status. Please try again later.', 'error');
        return null;
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching lecture detail:', error);
      showToast('Error refreshing lecture status.', 'error');
      return null;
    }
  };

  // Calculate credit cost based on file size
  const calculateCreditCost = (fileSizeBytes: number) => {
    if (fileSizeBytes) {
      const sizeMB = fileSizeBytes / (1024 * 1024);
      return Math.max(1, Math.floor(sizeMB / 1));
    }
    return 1;
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      setSelectedFile(files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      setSelectedFile(files[0]);
    }
  };

  const handleUploadClick = () => {
    if (selectedFile) {
      handleFileUpload(selectedFile);
    }
  };

  const exportLectureTxt = () => {
    const title = selectedLecture?.title || 'lecture';
    // Prefer the raw model output (no injected <style>/CSS); fall back to the
    // rendered HTML, which htmlToPlainText strips styles from safely.
    const raw = lectureContent?.content;
    const text = raw
      ? htmlToPlainText(isLikelyHtml(raw) ? raw : convertMarkdownToHtml(raw))
      : htmlToPlainText(defaultHtmlContent);
    const filename = `${title.replace(/[^a-z0-9-_]+/gi, '_')}.txt`;
    downloadText(filename, `${title}\n\n${text}`);
  };

  // Import a TXT previously exported from this module and open it in the
  // content view for revision — entirely client-side, no AI call needed.
  const handleImportLectureTxt = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow picking the same file again later
    if (!file) return;
    if (!/\.txt$/i.test(file.name)) {
      showToast('Please choose a .txt file exported from this module.', 'error');
      return;
    }
    try {
      const rawText = (await file.text()).replace(/^\uFEFF/, '').trim(); // strip BOM if present
      if (!rawText) {
        showToast('That file is empty.', 'error');
        return;
      }
      // Export format (see exportLectureTxt): `${title}\n\n${body}`
      const sepAt = rawText.indexOf('\n\n');
      const title = (sepAt > 0 ? rawText.slice(0, sepAt) : rawText.split('\n', 1)[0]).trim()
        || file.name.replace(/\.txt$/i, '');
      const body = (sepAt > 0 ? rawText.slice(sepAt + 2) : rawText.slice(title.length)).trim();
      if (!body) {
        showToast('No lecture content found in that file.', 'error');
        return;
      }

      // Escape before building HTML: the txt is user-supplied, and the content
      // view renders via dangerouslySetInnerHTML — don't let a crafted file
      // inject markup. Math ($...$) and **bold** survive escaping.
      const escapedBody = body
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
      const htmlContent = toDisplayHtml(escapedBody);
      const now = new Date().toISOString();
      setSelectedLecture({
        id: `imported-${Date.now()}`,
        title,
        original_filename: file.name,
        file_size: file.size,
        total_pages: 0,
        credit_cost: 0,
        status: 'completed',
        created_at: now,
        last_accessed: null,
        completed_at: now,
        error_message: '',
        pdf_file: '',
      });
      setLectureContent({ id: 'imported', content: body, created_at: now });
      setDefaultHtmlContent(htmlContent);
      setContentChunks(splitHtmlIntoChunks(htmlContent));
      setActiveChunkIndex(0);
      setCurrentView('content');
      showToast('Lecture imported — happy revising!', 'success');
    } catch (err) {
      console.error('TXT import failed', err);
      showToast('Could not read that file. Please try again.', 'error');
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    loadLectures(1, searchTerm);
  };

  const handlePageChange = (page: number) => {
    loadLectures(page, searchTerm);
  };

  const handleViewContent = async (lecture: Lecture) => {
    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.LECTURE_CONTENT(lecture.id)), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
      });

      if (response.ok) {
        const content = await response.json();
        if (lecture.status !== 'completed') {
          showToast('Lecture is still processing. Please try again later.', 'error');
          loadLectures();
          return;
        }

        setLectureContent(content);
        setSelectedLecture(lecture);
        const htmlContent = toDisplayHtml(content.content);
        const chunks = splitHtmlIntoChunks(htmlContent);
        setDefaultHtmlContent(htmlContent);
        setContentChunks(chunks);
        setActiveChunkIndex(0);
        setCurrentView('content');
      } else {
        showToast('Failed to load lecture content', 'error');
      }
    } catch (error) {
      console.error('Error loading content:', error);
      showToast('Error loading lecture content', 'error');
    }
  };

  const handleDeleteLecture = async () => {
    if (!lectureToDelete) return;

    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.LECTURE_DELETE(lectureToDelete.id)), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
      });

      if (response.ok) {
        showToast('Lecture deleted successfully', 'success');
        loadLectures();
        setShowDeleteModal(false);
        setLectureToDelete(null);
      } else {
        showToast('Failed to delete lecture', 'error');
      }
    } catch (error) {
      console.error('Delete error:', error);
      showToast('Error deleting lecture', 'error');
    }
  };

  const handleTitleDoubleClick = (lecture: Lecture) => {
    setEditingTitle(lecture.id);
    setEditTitleValue(lecture.title);
  };

  const handleTitleEdit = async (lectureId: string) => {
    if (!editTitleValue.trim()) {
      setEditingTitle(null);
      setEditTitleValue('');
      return;
    }

    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.LECTURE_UPDATE(lectureId)), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
        body: JSON.stringify({ title: editTitleValue.trim() }),
      });

      if (response.ok) {
        showToast('Title updated successfully', 'success');
        setLectures((prevLectures) =>
          prevLectures.map((lecture) =>
            lecture.id === lectureId ? { ...lecture, title: editTitleValue.trim() } : lecture
          )
        );
        setEditingTitle(null);
        setEditTitleValue('');
      } else {
        const error = await response.json().catch(() => ({}));
        showToast(error.error || 'Failed to update title', 'error');
      }
    } catch (error) {
      console.error('Title update error:', error);
      showToast('Error updating title', 'error');
    }
  };

  const handleTitleKeyPress = (e: React.KeyboardEvent, lectureId: string) => {
    if (e.key === 'Enter') {
      handleTitleEdit(lectureId);
    } else if (e.key === 'Escape') {
      setEditingTitle(null);
      // Reset the edit value to the current lecture title to avoid stale state
      const currentLecture = lectures.find((lecture) => lecture.id === lectureId);
      if (currentLecture) {
        setEditTitleValue(currentLecture.title);
      }
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Helper function to format processing time
  const formatProcessingTime = (seconds: number): string => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
  };

  // Main menu view
  const renderMainMenu = () => (
    <div className="lecture-main-menu">
      <div className="module-header lecture-module">
        <div className="module-icon">🧑‍🏫</div>
        <h1 className="module-title">{t('moduleHeaders.lectureMain.title')}</h1>
        <p className="module-subtitle">{t('moduleHeaders.lectureMain.subtitle')}</p>
      </div>

      <ModuleTopExtras moduleId="lecture" />

      <div className="lecture-guidelines">
        <div className="guideline-item">
          <div className="guideline-icon">📄</div>
          <div className="guideline-content">
            <h3>Upload PDF</h3>
            <p>Slides, handouts or scanned notes — PDF up to 25 MB (up to 80 pages)</p>
          </div>
        </div>
        <div className="guideline-item">
          <div className="guideline-icon">🖼️</div>
          <div className="guideline-content">
            <h3>AI reads every page</h3>
            <p>The AI visually reads each page — text, diagrams and scans alike (long notes are read in batches)</p>
          </div>
        </div>
        <div className="guideline-item">
          <div className="guideline-icon">🧑‍🏫</div>
          <div className="guideline-content">
            <h3>Lecture + export</h3>
            <p>Get a clear, explained write-up and export it as a TXT file</p>
          </div>
        </div>
      </div>

      <div className="lecture-actions">
        <div className="action-card" onClick={() => setCurrentView('upload')}>
          <div className="action-icon">➕</div>
          <h3>Generate Content</h3>
          <p>Upload a PDF and get AI-powered lecture explanations</p>
        </div>
        <div className="action-card" onClick={() => txtImportRef.current?.click()}>
          <div className="action-icon">📂</div>
          <h3>Revise from Export</h3>
          <p>Upload a TXT you exported here before to review it again</p>
        </div>
        <input
          ref={txtImportRef}
          type="file"
          accept=".txt,text/plain"
          style={{ display: 'none' }}
          onChange={handleImportLectureTxt}
        />
      </div>
    </div>
  );

  // Upload view
  const renderUploadView = () => {
    const fileSizeMB = selectedFile ? (selectedFile.size / (1024 * 1024)).toFixed(2) : 0;

    return (
      <div className="lecture-module">
        <div className="module-header">
          <div className="module-title">{t('moduleHeaders.lectureUpload.title')}</div>
          <div className="module-icon">🧑‍🏫</div>
        </div>

        <ModuleTopExtras moduleId="lecture" />
        
        <div className="lecture-upload">
          <div className="upload-header">
            <button 
              className="btn btn-secondary"
              onClick={() => setCurrentView('main')}
            >
              ← Back to Menu
            </button>
          </div>
          
          <div className="file-drop-zone">
            <div
              className={`drop-zone ${dragActive ? 'drag-active' : ''} ${uploading ? 'uploading' : ''}`}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf"
                onChange={handleFileInputChange}
                style={{ display: 'none' }}
              />
              
              {uploading ? (
                <div className="upload-progress">
                  <AdProcessingBanner active={uploading} />
                  <div className="progress-spinner"></div>
                  <p>Reading every page visually and writing your lecture…</p>
                  <p style={{ fontSize: '0.85rem', color: '#64748b', marginTop: 4 }}>
                    Long notes take a little while — please keep this tab open.
                  </p>

                  {/* Simple Timer Display */}
                  <div className="processing-status">
                    <div className="processing-timer">
                      <span className="timer-icon">⏱️</span>
                      <span className="timer-text">{formatProcessingTime(lectureProgress.processingTime)}</span>
                    </div>
                  </div>
                </div>
              ) : selectedFile ? (
                <div className="file-selected">
                  <div className="file-icon">📄</div>
                  <h3>{selectedFile.name}</h3>
                  <p>Size: {fileSizeMB} MB</p>
                  <button 
                    className="btn btn-secondary btn-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedFile(null);
                    }}
                  >
                    Change File
                  </button>
                </div>
              ) : (
                <div className="drop-zone-content">
                  <div className="drop-zone-icon">📄</div>
                  <h3>Drop your PDF here</h3>
                  <p>or click to browse files</p>
                  <div className="file-requirements">
                    <p>• PDF files only (slides, handouts or scans)</p>
                    <p>• Maximum {MAX_UPLOAD_MB}MB • up to {MAX_LECTURE_PAGES} pages read</p>
                    <p>• Every page is read visually — long notes take a little longer</p>
                    <p>• Free to use • kept in this tab only</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {selectedFile && (
            <div className="lecture-lang">
              <label className="lecture-lang__label" htmlFor="lecture-language">
                <span className="lecture-lang__icon" aria-hidden="true">🌐</span>
                Output language
              </label>
              <div className="lecture-lang__wrap">
                <select
                  id="lecture-language"
                  className="lecture-lang__select"
                  value={outputLanguage}
                  onChange={(e) => setOutputLanguage(e.target.value as LectureLanguage)}
                  disabled={uploading}
                >
                  <option value="auto">Auto-detect · same language as the PDF</option>
                  <option value="traditional_chinese">繁體中文 Traditional Chinese</option>
                  <option value="simplified_chinese">简体中文 Simplified Chinese</option>
                  <option value="english">English</option>
                </select>
              </div>
            </div>
          )}

          {selectedFile && (
            <div className="upload-actions">
              <button
                className="btn btn-primary btn-large"
                onClick={handleUploadClick}
                disabled={uploading}
              >
                {uploading ? 'Processing...' : 'Generate Lecture'}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  // History view
  const renderHistoryView = () => {
    const completedLectures = lectures.filter((lecture) => lecture.status === 'completed');
    return (
      <div className="lecture-history">
        <div className="module-header lecture-module">
          <div className="module-icon">🧑‍🏫</div>
          <h1 className="module-title">{t('moduleHeaders.lectureHistory.title')}</h1>
          <p className="module-subtitle">{t('moduleHeaders.lectureHistory.subtitle')}</p>
        </div>

        <ModuleTopExtras moduleId="lecture" />
        
        <div className="history-content">
          <div className="history-navigation">
            <button 
              className="back-button"
              onClick={() => setCurrentView('main')}
            >
              ← Back to Menu
            </button>
          </div>
          
          
          <div className="lecture-list">
            <div className="list-header">
              <h3>Your Lectures</h3>
              <form onSubmit={handleSearch} className="search-form">
                <input
                  type="text"
                  placeholder="Search lectures..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
                <button type="submit" className="btn btn-primary">Search</button>
              </form>
            </div>
            
            {completedLectures.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">📚</div>
                <h3>No completed lectures</h3>
                <p>Upload a PDF and check back after processing completes.</p>
                <button 
                  className="btn btn-primary"
                  onClick={() => setCurrentView('upload')}
                >
                  Upload Lecture
                </button>
              </div>
            ) : (
              <div className="lecture-grid">
                {completedLectures.map((lecture) => (
                  <div key={lecture.id} className="lecture-card">
                    <div className="lecture-info">
                      {editingTitle === lecture.id ? (
                        <input
                          type="text"
                          value={editTitleValue}
                          onChange={(e) => setEditTitleValue(e.target.value)}
                          onBlur={() => handleTitleEdit(lecture.id)}
                          onKeyDown={(e) => handleTitleKeyPress(e, lecture.id)}
                          className="lecture-title-edit"
                          autoFocus
                        />
                      ) : (
                        <h3 
                          onDoubleClick={() => handleTitleDoubleClick(lecture)}
                          className="lecture-title-editable"
                          title="Double-click to edit"
                        >
                          {lecture.title}
                        </h3>
                      )}
                      <p className="lecture-filename">
                        {lecture.original_filename.length > 24
                          ? lecture.original_filename.slice(0, 24) + '...' + lecture.original_filename.slice(-24)
                          : lecture.original_filename}
                      </p>
                      <div className="lecture-meta">
                        <span>Pages: {lecture.total_pages}</span>
                        <span>Size: {formatFileSize(lecture.file_size)}</span>
                        <span>Created: {formatDate(lecture.created_at)}</span>
                      </div>
                    </div>

                    <div className="lecture-actions">
                      <button
                        className="btn btn-primary"
                        onClick={() => handleViewContent(lecture)}
                      >
                        View Content
                      </button>
                      <button
                        className="btn btn-secondary"
                        onClick={() => openPdfInNewTab(lecture.id, lecture.original_filename)}
                      >
                        View PDF
                      </button>
                      <button
                        className="btn btn-danger"
                        onClick={() => {
                          setLectureToDelete(lecture);
                          setShowDeleteModal(true);
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {totalPages > 1 && (
              <div className="pagination">
                <button
                  className="btn btn-secondary"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                <span className="page-info">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  className="btn btn-secondary"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Content view
  const renderContentView = () => {
    if (!selectedLecture || !lectureContent) return null;

    const totalChunks = contentChunks.length;
    const activeChunk = totalChunks > 0 ? contentChunks[activeChunkIndex] : null;
    const signals = getContentSignals(activeChunk ? activeChunk.content : defaultHtmlContent);

    return (
      <div className="lecture-content">
        <style>{CONTENT_STYLES}</style>
        <div className="content-header">
          <h2>{selectedLecture.title}</h2>
          <div className="content-actions">
            <button
              className="btn btn-secondary"
              onClick={() => setCurrentView('upload')}
            >
              ← New Lecture
            </button>
            <button
              className="btn btn-secondary"
              onClick={exportLectureTxt}
            >
              Export TXT
            </button>
          </div>
        </div>

        <div className="content-meta">
          {selectedLecture.total_pages > 0 && <span>Pages: {selectedLecture.total_pages}</span>}
          {totalChunks > 1 && (
            <span>
              Section {activeChunkIndex + 1} of {totalChunks}
            </span>
          )}
        </div>

        <div className="lecture-content-layout">
          {totalChunks > 1 && (
            <aside className="lecture-toc">
              <h4>Contents</h4>
              <ul className="toc-list">
                {contentChunks.map((chunk, idx) => (
                  <li
                    key={chunk.id}
                    className={`toc-item ${idx === activeChunkIndex ? 'active' : ''}`}
                    onClick={() => setActiveChunkIndex(idx)}
                    title={stripHtml(chunk.title)}
                  >
                    <span className="toc-index">{idx + 1}</span>
                    <span>{stripHtml(chunk.title)}</span>
                  </li>
                ))}
              </ul>
            </aside>
          )}

          <main className="lecture-main-pane">
            <div className="lecture-note">
              <strong>AI-generated content</strong> — For reference only; check your own materials for accuracy.
            </div>

            <div className="lecture-section-card">
              <div className="section-card-header">
                <h3>{activeChunk ? activeChunk.title : 'Lecture Content'}</h3>
                <div className="section-badges">
                  {signals.hasTable && (
                    <span className="badge badge-blue"><span className="dot"></span> Tables</span>
                  )}
                  {signals.hasList && (
                    <span className="badge badge-emerald"><span className="dot"></span> Lists</span>
                  )}
                  {signals.hasCode && (
                    <span className="badge badge-amber"><span className="dot"></span> Code</span>
                  )}
                  {signals.hasQuote && (
                    <span className="badge badge-fuchsia"><span className="dot"></span> Notes</span>
                  )}
                  {!signals.hasTable && !signals.hasList && !signals.hasCode && !signals.hasQuote && (
                    <span className="badge badge-slate"><span className="dot"></span> Text</span>
                  )}
                </div>
              </div>
              <div className="section-card-body">
                {activeChunk ? (
                  <div className="lecture-content-body">
                    {processMathContent(activeChunk.content)}
                  </div>
                ) : (
                  <div className="lecture-content-body">
                    {processMathContent(defaultHtmlContent)}
                  </div>
                )}
              </div>
            </div>

            {totalChunks > 1 && (
              <div className="content-pagination">
                <button
                  className="btn btn-secondary"
                  onClick={() => setActiveChunkIndex((prev) => Math.max(prev - 1, 0))}
                  disabled={activeChunkIndex === 0}
                >
                  ← Previous Section
                </button>
                <div className="content-page-indicator">
                  Section {activeChunkIndex + 1} of {totalChunks}
                </div>
                <button
                  className="btn btn-secondary"
                  onClick={() => setActiveChunkIndex((prev) => Math.min(prev + 1, totalChunks - 1))}
                  disabled={activeChunkIndex === totalChunks - 1}
                >
                  Next Section →
                </button>
              </div>
            )}
          </main>
        </div>
      </div>
    );
  };

  // Render current view
  const renderCurrentView = () => {
    switch (currentView) {
      case 'main': return renderMainMenu();
      case 'upload': return renderUploadView();
      case 'content': return renderContentView();
      default: return renderMainMenu();
    }
  };

  return (
    <div className="lecture-module">
      {renderCurrentView()}
      
      {showDeleteModal && lectureToDelete && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <div className="modal-icon">
                <TrashIcon size={24} />
              </div>
              <h3>Delete Lecture</h3>
            </div>
            <p>Are you sure you want to delete "{lectureToDelete.title}"?</p>
            <p className="warning-text">This will permanently delete the lecture and its PDF file.</p>
            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowDeleteModal(false);
                  setLectureToDelete(null);
                }}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={handleDeleteLecture}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Toast notifications */}
      <div className="toast-container">
        {toasts.map((toast) => (
          <div key={toast.id} className={`toast toast-${toast.type}`}>
            {toast.message}
          </div>
        ))}
      </div>
    </div>
  );
};

export default LectureModule;